import { TextMergeConfiguration } from "./TextMergeConfiguration";
import { BaseTextMergeElement } from "./Elements/BaseTextMergeElement";
import { BaseTextMergeContainerElement } from "./Elements/BaseTextMergeContainerElement";
import { TextElementRegistration } from "./RegisterTextElement";
import { TextElement } from "./Elements/Text/TextElement";
import { IContext } from "../Common/IContext";
import { TextMergeBuilderPage } from "./TextMergeBuilderPage";
import { IDesignPanelConfig } from "../Common/IDesignPanelConfig";
import { StateManager } from "../Common/StateManager";
import type { AppGlueAny } from "../Common/AppGlueAny";

export class TextMergeEditContext implements IContext {
    definition: TextMergeConfiguration;
    mode: TextMergeBuilderPage = TextMergeBuilderPage.TextEditor;
    title?: string;
    designConfig?: IDesignPanelConfig;
    runtimeConfig?: IDesignPanelConfig;
    public onSave?: (data: TextMergeConfiguration) => void;
    public onCancel?: () => void;
    clipboardElement: AppGlueAny;
    lastSelectionElement: AppGlueAny;

    constructor(def: TextMergeConfiguration) {
        this.definition = def;
    }

    private _cursorPosition?: number;

    get cursorPosition(): number | undefined {
        return this._cursorPosition;
    }

    set cursorPosition(value: number | undefined) {
        if (value === undefined) return;
        this._cursorPosition = value;
        StateManager.propertyChanged(this, "cursorPosition");
    }

    private _selectedElement?: BaseTextMergeElement;

    get selectedElement(): BaseTextMergeElement | undefined {
        return this._selectedElement;
    }

    setSelectedElement(value: BaseTextMergeElement) {
        if (this.selectedElement !== value) {
            this.selectedElement?.onDeselect();
            this._selectedElement = value;
            this._cursorPosition = undefined;
            StateManager.propertiesChanged(this, ["selectedElement"]);
        }
    }

    deleteSelected() {
        if (this._selectedElement && this.selectedElement?.parent) {
            this._selectedElement.parent!.removeElement(this._selectedElement);
        }
    }

    moveUp() {
        if (this._selectedElement && this.selectedElement?.parent) {
            let index = this._selectedElement.parent!.elements.indexOf(this._selectedElement);

            if (index === -1) {
                // missing
                throw new Error("selected element not found in parent container");
            } else if (index === 0) {
                if (this.selectedElement.parent.parent !== undefined) {
                    // put in parent
                    this._selectedElement.parent!.removeElementAt(index);
                    // find index of parent in parent

                    let parentIndex = this.selectedElement.parent.parent.elements.indexOf(this.selectedElement.parent);

                    if (parentIndex === -1) {
                        throw new Error("Parent not found in Super Parents list");
                    }

                    let newParent = this._selectedElement.parent!.parent!;
                    let oldParent = this._selectedElement.parent!;

                    newParent.addElement(this._selectedElement, parentIndex);

                    this._selectedElement.parent = newParent;
                    StateManager.propertyChanged(newParent, "elements");
                    StateManager.propertyChanged(oldParent, "elements");
                }
            } else {
                // check if parent is a container

                if (this._selectedElement.parent?.elements[index - 1] instanceof BaseTextMergeContainerElement) {
                    // add to end
                    this._selectedElement.parent!.removeElementAt(index);
                    let elementAbove = this._selectedElement.parent!.elements[
                        index - 1
                    ] as BaseTextMergeContainerElement;
                    let oldParent = this._selectedElement.parent!;
                    elementAbove.addElement(this._selectedElement);
                    this._selectedElement.parent = elementAbove;
                    StateManager.propertyChanged(elementAbove, "elements");
                    StateManager.propertyChanged(oldParent, "elements");
                } else {
                    this._selectedElement.parent!.removeElementAt(index);
                    this._selectedElement.parent!.addElement(this._selectedElement, index - 1);
                    StateManager.propertyChanged(this._selectedElement.parent!, "elements");
                }
            }
        }
    }

    moveDown() {
        if (this._selectedElement && this.selectedElement?.parent) {
            let index = this._selectedElement.parent!.elements.indexOf(this._selectedElement);

            if (index === -1) {
                // missing
                throw new Error("selected element not found in parent container");
            } else if (index === this.selectedElement.parent.elements.length - 1) {
                // last element
                if (this.selectedElement.parent.parent !== undefined) {
                    // put in parent
                    this._selectedElement.parent!.removeElementAt(index);
                    // find index of parent in parent

                    let parentIndex = this.selectedElement.parent.parent.elements.indexOf(this.selectedElement.parent);

                    if (parentIndex === -1) {
                        throw new Error("Parent not found in Super Parents list");
                    }

                    let newParent = this._selectedElement.parent!.parent!;
                    let oldParent = this._selectedElement.parent!;

                    newParent.addElement(this._selectedElement, parentIndex + 1);

                    this._selectedElement.parent = newParent;
                    StateManager.propertyChanged(oldParent, "elements");
                    StateManager.propertyChanged(newParent, "elements");
                }
            } else {
                if (this._selectedElement.parent?.elements[index + 1] instanceof BaseTextMergeContainerElement) {
                    // add to end
                    let elementBelow = this._selectedElement.parent!.elements[
                        index + 1
                    ] as BaseTextMergeContainerElement;

                    this._selectedElement.parent!.removeElementAt(index);

                    let oldParent = this._selectedElement.parent!;

                    elementBelow.addElement(this._selectedElement, 0);
                    this._selectedElement.parent = elementBelow;
                    StateManager.propertyChanged(elementBelow, "elements");
                    StateManager.propertyChanged(oldParent, "elements");
                } else {
                    this._selectedElement.parent!.removeElementAt(index);
                    this._selectedElement.parent!.addElement(this._selectedElement, index + 1);
                    StateManager.propertyChanged(this._selectedElement.parent!, "elements");
                }
            }
        }
    }

    clearSelection() {
        this._selectedElement = undefined;
    }

    // todo: split text element if inserting at position
    addElement(
        type: string,
        relativePostion: "below" | "above" | "inline" = "below",
        _position?: number,
    ): BaseTextMergeElement {
        let reg = TextElementRegistration.getElement(type);

        if (!reg) throw new Error("Text element of type " + type + " was not found");

        let newText = reg.create();

        if (this.selectedElement instanceof BaseTextMergeContainerElement) {
            this.selectedElement.addElement(newText, this.selectedElement.elements.length);

            newText.parent = this.selectedElement;

            this.setSelectedElement(newText);
        } else {
            if (!this._selectedElement) {
                this.definition.rootElement.addElement(newText, this.definition.rootElement.elements.length);

                newText.parent = this.definition.rootElement;

                this.setSelectedElement(newText);
            } else {
                let parent = this._selectedElement.parent!;

                newText.parent = parent;

                const index = this.selectedElement ? parent.elements.indexOf(this.selectedElement, 0) : -1;

                if (index !== -1) {
                    if (
                        relativePostion === "inline" &&
                        this.selectedElement?.__type === new TextElement().__type &&
                        this._cursorPosition !== undefined
                    ) {
                        let textElement: TextElement = this.selectedElement as TextElement;
                        if (this._cursorPosition === 0 || textElement.text === undefined) {
                            // insert above
                            parent.addElement(newText, index);
                        } else if (
                            textElement.text.length === 0 ||
                            this._cursorPosition === textElement.text?.length - 1
                        ) {
                            // insert below
                            parent.addElement(newText, index + 1);
                        } else {
                            // inserting inline
                            let beforeText = textElement.text?.substring(0, this.cursorPosition);
                            let afterText = textElement.text?.substring(this.cursorPosition!);

                            textElement.text = beforeText;
                            parent.addElement(newText, index + 1);
                            let afterTextElement = new TextElement();
                            afterTextElement.text = afterText;
                            parent.addElement(afterTextElement, index + 2);
                        }
                    } else {
                        if (relativePostion === "above") {
                            parent.addElement(newText, index);
                        } else {
                            parent.addElement(newText, index + 1);
                        }
                    }
                } else {
                    parent.addElement(newText, parent.elements.length - 1);
                }
                this.setSelectedElement(newText);
            }
        }

        return newText;
    }

    onCopy(_id: string | undefined): void {}

    onCut(_id: string | undefined): void {}

    onDelete(_id: string | undefined): void {}

    onPaste(_id: string | undefined): void {}
}
